/**
 * QR Scanner component using html5-qrcode
 * Optimized for mobile (iOS and Android)
 * iOS improvements: larger scan area, better camera handling
 */
import { useEffect, useRef, useState, useCallback } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { motion } from 'framer-motion';
import { Camera, X, QrCode, SwitchCamera, Loader2, RefreshCw } from 'lucide-react';
import { Button } from './ui/button';
import { usePermissionsStore } from '../store';

// Detect iOS
const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

export const QRScanner = ({ onScan, onClose }) => {
  const scannerRef = useRef(null);
  const isRunningRef = useRef(false);
  const [error, setError] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [permissionState, setPermissionState] = useState('prompt'); // 'prompt', 'granted', 'denied'
  const [cameras, setCameras] = useState([]);
  const [currentCameraIndex, setCurrentCameraIndex] = useState(0);
  const [isStarting, setIsStarting] = useState(true);
  
  // Use permissions store to track granted permissions
  const { cameraGranted, setCameraGranted, checkBrowserPermissions } = usePermissionsStore();
  
  // Check browser permissions on mount and log state
  useEffect(() => {
    console.log('[QRScanner] Initial cameraGranted state:', cameraGranted);
    checkBrowserPermissions();
  }, [cameraGranted, checkBrowserPermissions]);

  // Safely stop the scanner
  const stopScanner = useCallback(async () => {
    if (scannerRef.current && isRunningRef.current) {
      try {
        await scannerRef.current.stop();
        isRunningRef.current = false;
      } catch (e) {
        // Ignore stop errors
        console.log('Scanner stop (safe):', e.message);
      }
    }
  }, []);

  // Request camera permission and start scanner
  const startScanner = useCallback(async (cameraId = null) => {
    try {
      setIsStarting(true);
      setError(null);
      
      // Stop any existing scanner first
      await stopScanner();
      
      // Get available cameras
      let devices;
      try {
        devices = await Html5Qrcode.getCameras();
      } catch (camError) {
        console.error('Camera access error:', camError);
        setPermissionState('denied');
        setError('No se pudo acceder a la cámara. Verifica los permisos.');
        setIsStarting(false);
        return;
      }
      
      if (!devices || devices.length === 0) {
        setError('No se encontraron cámaras disponibles');
        setPermissionState('denied');
        setIsStarting(false);
        return;
      }
      
      setCameras(devices);
      setPermissionState('granted');
      console.log('[QRScanner] Camera access granted, saving to store...');
      setCameraGranted(true); // Save to persistent store
      
      // Prefer back camera on mobile
      let selectedCameraId = cameraId;
      if (!selectedCameraId) {
        const backCamera = devices.find(d => 
          d.label.toLowerCase().includes('back') || 
          d.label.toLowerCase().includes('trasera') ||
          d.label.toLowerCase().includes('rear') ||
          d.label.toLowerCase().includes('environment')
        );
        selectedCameraId = backCamera?.id || devices[0].id;
        
        // Update camera index
        const idx = devices.findIndex(d => d.id === selectedCameraId);
        setCurrentCameraIndex(idx >= 0 ? idx : 0);
      }
      
      // Create scanner instance if needed
      if (!scannerRef.current) {
        scannerRef.current = new Html5Qrcode('qr-reader', {
          verbose: false,
          experimentalFeatures: {
            useBarCodeDetectorIfSupported: true
          }
        });
      }
      
      // Get actual container dimensions
      const container = document.getElementById('qr-reader');
      const containerWidth = container?.clientWidth || window.innerWidth;
      const containerHeight = container?.clientHeight || window.innerHeight;
      
      // qrbox should be relative to the video/container size, not screen size
      // Use 85% of the smaller dimension for the scan area
      const minDimension = Math.min(containerWidth, containerHeight);
      
      let qrboxSize;
      if (isIOS) {
        // iOS: widen scan area aggressively for Safari/Chrome
        qrboxSize = Math.floor(minDimension * 0.90);
        qrboxSize = Math.max(320, qrboxSize);
      } else {
        // Android: keep current stable behavior
        qrboxSize = Math.floor(minDimension * 0.70);
        qrboxSize = Math.max(220, Math.min(qrboxSize, 350));
      }
      
      console.log(`[QRScanner] Container: ${containerWidth}x${containerHeight}, qrbox: ${qrboxSize}, iOS: ${isIOS}`);
      
      // Configuration optimized for each platform
      const scannerConfig = {
        fps: isIOS ? 15 : 10,
        qrbox: { width: qrboxSize, height: qrboxSize },
        // Don't force aspect ratio - let the camera decide
        aspectRatio: undefined,
        disableFlip: false,
        // iOS Safari works better with environment camera
        ...(isIOS ? {
          videoConstraints: {
            facingMode: { ideal: 'environment' },
            // Request higher resolution for better QR detection
            width: { ideal: 1920, min: 640 },
            height: { ideal: 1080, min: 480 }
          }
        } : {})
      };
      
      await scannerRef.current.start(
        selectedCameraId,
        scannerConfig,
        (decodedText) => {
          handleQRDetected(decodedText);
        },
        () => {
          // Ignore scan errors (no QR found)
        }
      );
      
      isRunningRef.current = true;
      setScanning(true);
      setIsStarting(false);
      
    } catch (err) {
      console.error('Scanner error:', err);
      setIsStarting(false);
      isRunningRef.current = false;
      
      if (err.name === 'NotAllowedError' || err.message?.includes('Permission')) {
        setPermissionState('denied');
        setCameraGranted(false); // Update persistent store
        setError('Permiso de cámara denegado. Habilita el acceso en la configuración.');
      } else if (err.name === 'NotFoundError' || err.message?.includes('Requested device not found')) {
        setError('No se encontró la cámara seleccionada');
      } else if (err.name === 'NotReadableError' || err.message?.includes('Could not start')) {
        setError('La cámara está siendo usada por otra aplicación');
      } else if (err.message?.includes('already running')) {
        // Scanner already running, that's fine
        setScanning(true);
        setIsStarting(false);
      } else {
        setError('Error al iniciar la cámara. Intenta de nuevo.');
      }
    }
  }, [stopScanner]); // handleQRDetected is intentionally excluded to avoid circular dependency

  // Handle QR code detection
  const handleQRDetected = useCallback(async (decodedText) => {
    // Parse QR data
    try {
      const data = JSON.parse(decodedText);
      if (data.club_id && data.court_id) {
        // Valid QR - stop scanner and notify parent
        await stopScanner();
        onScan(data);
        return;
      }
    } catch (e) {
      // Not JSON, try URL format
    }
    
    // Try URL format (legacy)
    try {
      const url = new URL(decodedText);
      const clubId = url.searchParams.get('club_id') || url.searchParams.get('club');
      const courtId = url.searchParams.get('court_id') || url.searchParams.get('court');
      
      if (clubId && courtId) {
        await stopScanner();
        onScan({ club_id: clubId, court_id: courtId });
        return;
      }
    } catch (e2) {
      // Not a valid URL either
    }
    
    // Invalid QR
    setError('Código QR no válido para Clipper');
  }, [onScan, stopScanner]);

  // Switch camera
  const switchCamera = useCallback(async () => {
    if (cameras.length <= 1) return;
    
    const nextIndex = (currentCameraIndex + 1) % cameras.length;
    setCurrentCameraIndex(nextIndex);
    
    await startScanner(cameras[nextIndex].id);
  }, [cameras, currentCameraIndex, startScanner]);

  // Retry starting scanner
  const handleRetry = useCallback(() => {
    setError(null);
    setPermissionState('prompt');
    startScanner();
  }, [startScanner]);

  // Handle close
  const handleClose = useCallback(async () => {
    await stopScanner();
    onClose();
  }, [stopScanner, onClose]);

  // Start scanner on mount
  useEffect(() => {
    let mounted = true;
    
    const initScanner = async () => {
      // Small delay to ensure DOM is ready
      await new Promise(resolve => setTimeout(resolve, 100));
      if (mounted) {
        await startScanner();
      }
    };
    
    initScanner();
    
    return () => {
      mounted = false;
      // Cleanup on unmount
      if (scannerRef.current && isRunningRef.current) {
        scannerRef.current.stop().catch(() => {});
        isRunningRef.current = false;
      }
    };
  }, []);

  // Handle permission denied - show instructions
  const renderPermissionDenied = () => (
    <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
      <div className="w-20 h-20 rounded-full bg-destructive/10 flex items-center justify-center mb-4">
        <Camera className="w-10 h-10 text-destructive" />
      </div>
      <h3 className="font-semibold text-lg mb-2">Acceso a cámara requerido</h3>
      <p className="text-muted-foreground text-sm mb-6">
        Para escanear códigos QR, necesitamos acceso a tu cámara.
      </p>
      
      <div className="bg-card rounded-lg p-4 text-left text-sm mb-6 w-full max-w-xs">
        <p className="font-medium mb-2">Cómo habilitar:</p>
        <ul className="list-disc list-inside text-muted-foreground space-y-1">
          <li><strong>iOS Safari:</strong> Configuración → Safari → Cámara → Permitir</li>
          <li><strong>Android Chrome:</strong> Toca el ícono de candado 🔒 en la barra de direcciones</li>
          <li>Luego recarga la página</li>
        </ul>
      </div>
      
      <Button onClick={handleRetry} className="mb-3 gap-2">
        <RefreshCw className="w-4 h-4" />
        Reintentar
      </Button>
      <Button variant="ghost" onClick={handleClose}>
        Cancelar
      </Button>
    </div>
  );
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-background flex flex-col"
      data-testid="qr-scanner"
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-center gap-3">
          <QrCode className="w-6 h-6 text-primary" />
          <h2 className="text-lg font-semibold">Escanear código QR</h2>
        </div>
        <div className="flex items-center gap-2">
          {cameras.length > 1 && scanning && (
            <Button
              variant="ghost"
              size="icon"
              onClick={switchCamera}
              title="Cambiar cámara"
            >
              <SwitchCamera className="w-5 h-5" />
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={handleClose}
            data-testid="close-scanner-btn"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>
      </div>
      
      {permissionState === 'denied' && !isStarting ? (
        renderPermissionDenied()
      ) : (
        <>
          {/* Scanner container - iOS optimized for larger scan area */}
          <div className={`flex-1 flex flex-col items-center justify-center ${isIOS ? 'px-0' : 'p-2'}`}>
            <div 
              className="relative w-full"
              style={{ 
                maxWidth: isIOS ? '100vw' : '90vw',
                height: isIOS ? '78vh' : '60vh'
              }}
            >
              {/* QR Reader container - larger for iOS with full width */}
              <div 
                id="qr-reader" 
                className="w-full h-full rounded-2xl overflow-hidden bg-black"
              />
              
              {/* Loading overlay */}
              {isStarting && (
                <div className="absolute inset-0 bg-black/90 rounded-2xl flex flex-col items-center justify-center">
                  <Loader2 className="w-10 h-10 text-primary animate-spin mb-3" />
                  <p className="text-sm text-white">Iniciando cámara...</p>
                </div>
              )}
              
              {/* Overlay frame - Enhanced for better visibility on iOS */}
              {scanning && !isStarting && (
                <div className="absolute inset-0 pointer-events-none">
                  <div className="absolute inset-0 border-4 border-primary/30 rounded-2xl" />
                  {/* Corner accents - larger on iOS for better visibility */}
                  <div className={`absolute top-0 left-0 border-t-4 border-l-4 border-primary rounded-tl-2xl ${isIOS ? 'w-16 h-16' : 'w-12 h-12'}`} />
                  <div className={`absolute top-0 right-0 border-t-4 border-r-4 border-primary rounded-tr-2xl ${isIOS ? 'w-16 h-16' : 'w-12 h-12'}`} />
                  <div className={`absolute bottom-0 left-0 border-b-4 border-l-4 border-primary rounded-bl-2xl ${isIOS ? 'w-16 h-16' : 'w-12 h-12'}`} />
                  <div className={`absolute bottom-0 right-0 border-b-4 border-r-4 border-primary rounded-br-2xl ${isIOS ? 'w-16 h-16' : 'w-12 h-12'}`} />
                  
                  {/* Scanning line animation */}
                  <motion.div
                    initial={{ top: '10%' }}
                    animate={{ top: '90%' }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      repeatType: 'reverse',
                      ease: 'easeInOut',
                    }}
                    className="absolute left-4 right-4 h-0.5 bg-gradient-to-r from-transparent via-primary to-transparent"
                  />
                </div>
              )}
            </div>
            
            {/* Instructions & Errors */}
            <div className="mt-6 text-center">
              {error ? (
                <div className="space-y-3">
                  <p className="text-destructive text-sm">{error}</p>
                  <Button size="sm" variant="outline" onClick={handleRetry} className="gap-2">
                    <RefreshCw className="w-4 h-4" />
                    Reintentar
                  </Button>
                </div>
              ) : (
                <p className="text-muted-foreground text-sm">
                  Apunta la cámara al código QR de la cancha
                </p>
              )}
            </div>
            
            {/* Camera info */}
            {cameras.length > 1 && scanning && (
              <p className="text-xs text-muted-foreground mt-2">
                Cámara: {cameras[currentCameraIndex]?.label?.substring(0, 30) || 'Cámara ' + (currentCameraIndex + 1)}
              </p>
            )}
          </div>
          
          {/* Footer */}
          <div className="p-4 border-t border-border">
            <p className="text-xs text-muted-foreground text-center">
              Escanea el código QR ubicado en la cancha
            </p>
          </div>
        </>
      )}
    </motion.div>
  );
};

export default QRScanner;
